import type { ComponentType } from 'react';
import { useMemo } from 'react';
import type { ScaleTime } from 'd3-scale';
import { format as formatDate } from '../../../utils/formatting';
import {
    LastContactIndicator,
    LastRecordIndicator,
} from '../baseContactIndicator/BaseContactIndicator';
import { CALENDAR_SETTINGS, TEMPLATE_SETTINGS } from '../constants';
import SelectionWrapper from '../selectionWrapper/SelectionWrapper';
import TimeIndicator from '../timeIndicator/TimeIndicator';
import type { Day } from './useCalendarScales';
import type { BlockToRender, TimeWindow } from '../utils';
import {
    createTimeSpanObject,
    extendTemplate,
    formatTemplateData,
    generateBlocksPerDay,
} from '../utils';
import type { RefRegistry } from '../ref-registry';

function createSelectionWrapperConfig(
    data: BlockToRender['data'],
    elementProps: BlockToRender['elementProps']
): BlockToRender {
    return {
        data,
        Element: SelectionWrapper as ComponentType,
        elementProps,
    };
}

interface UseBlockRenderingParams {
    scalesPerDay: Map<Day, ScaleTime<number, number>> | null;
    timezone: string;
    data: BlockToRender['data'];
    currentDate: Date;
    unit?: string;
    buildMode?: boolean;
    lastContactDate: Date | null;
    lastRecordDate: Date | null;
    lastRecordUploads?: unknown;
    measuringPointName?: string;
    drawingBlock?: TimeWindow;
    openEditModal?: (timeWindow: TimeWindow) => void;
    currentBlock?: TimeWindow;
    refRegistry: RefRegistry;
}

export function useBlockRendering({
    scalesPerDay,
    timezone,
    data,
    currentDate,
    unit,
    buildMode,
    lastContactDate,
    lastRecordDate,
    lastRecordUploads,
    measuringPointName,
    drawingBlock,
    openEditModal,
    currentBlock,
    refRegistry,
}: UseBlockRenderingParams) {
    const commonElementProps = useMemo(
        () => ({
            unit,
            buildMode,
            openEditModal,
            currentBlock,
            refRegistry,
        }),
        [unit, buildMode, openEditModal, currentBlock, refRegistry]
    );

    const currentBlockPerDay = useMemo(() => {
        const template = extendTemplate(
            drawingBlock ? [drawingBlock] : [],
            TEMPLATE_SETTINGS.EXTEND_TEMPLATE_DAYS
        );
        return formatTemplateData(template);
    }, [drawingBlock]);

    const allBlocksPerDay = useMemo(() => {
        if (!scalesPerDay) return new Map();
        const scaleDays = Array.from(scalesPerDay.keys());

        const blocksToRender = [
            createSelectionWrapperConfig(currentBlockPerDay, commonElementProps),
            createSelectionWrapperConfig(data, commonElementProps),
            {
                data: [createTimeSpanObject(currentDate, currentDate)],
                Element: TimeIndicator,
            },
        ];

        if (lastContactDate) {
            const calendarEndDate = scaleDays[CALENDAR_SETTINGS.DAYS_IN_WEEK - 1].end;
            // lastContactDate can be in the future relative to the current calendar
            // view window. That would make a range error in other pieces of the code.
            // If it's in the "future", we're not even showing it, so just skip.
            if (lastContactDate < calendarEndDate) {
                blocksToRender.push({
                    data: [createTimeSpanObject(lastContactDate, calendarEndDate)],
                    Element: LastContactIndicator as ComponentType,
                    elementProps: {
                        title: measuringPointName,
                        date: formatDate(lastContactDate, { timezone }),
                    },
                });
            }
            if (
                // lastRecordDate will be unknown at first.
                lastRecordDate &&
                // Even though lastContactDate is out off range of the calendar,
                // lastRecordDate could still be in view.
                lastRecordDate < calendarEndDate &&
                // It's highly unlikely that lastRecordDate is past
                // lastContactDate (measurements in the future), but
                // we've had devices do strange records in the past.
                // Make sure to not crash on that.
                lastRecordDate < lastContactDate
            ) {
                blocksToRender.push({
                    data: [createTimeSpanObject(lastRecordDate, lastContactDate)],
                    Element: LastRecordIndicator as ComponentType,
                    elementProps: {
                        title: measuringPointName,
                        uploadData: lastRecordUploads || [],
                        date: formatDate(lastRecordDate, { timezone }),
                    },
                });
            }
        }

        return generateBlocksPerDay({ scalesPerDay, timezone, blocksToRender });
    }, [
        scalesPerDay,
        currentDate,
        data,
        lastContactDate,
        lastRecordDate,
        timezone,
        measuringPointName,
        lastRecordUploads,
        commonElementProps,
        currentBlockPerDay,
    ]);

    return allBlocksPerDay;
}
